import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { dbManager } from '@/indexdb/chat'
import { markConversationAsRead } from '@/api/chat'
import { getUserInfo, getRelationWithId, getAnchorStatus } from '@/api/user'
import { messageSendManager } from '@/managers/MessageSendManager'
import { useConversationStore } from '@/stores/conversation'
import { IMContentDecrypt } from '@/utils/crypto'
import { MSG_READ_TYPE, SPECIAL_USER_IDS } from '@/common/constants/message'
import { dateFormater, isToday } from '@/utils'

const TIME_THRESHOLD = 5 * 60 * 1000 // 5分钟（毫秒）

/**
 * 格式化单条消息（只负责解密和解析内容）
 */
const decryptMessage = (message) => {
  let content = message.content

  // 如果 content 已经是对象，说明已经解密过，直接返回
  if (typeof content === 'object' && content !== null) {
    return { ...message, content }
  }

  // 如果是字符串，进行解密和解析
  if (typeof content === 'string') {
    content = IMContentDecrypt(content)
    try {
      content = JSON.parse(content)
    } catch (error) {
      console.error('[Chat Store] Failed to parse message content:', error)
    }
  }

  return {
    ...message,
    content,
  }
}

/**
 * 批量格式化消息，并添加时间显示逻辑
 * @param {Array} messages - 消息数组（已按时间排序）
 * @returns {Array} 格式化后的消息数组，包含 showTime、formattedTime 和 isToday
 */
const formatMessagesWithTime = (messages, prevMsg = null) => {
  if (!messages || messages.length === 0) {
    return []
  }

  return messages.map((msg, index) => {
    // 先解密和解析内容
    const formattedMsg = decryptMessage(msg)

    let showTime = false

    // 规则1: 第一条消息必须显示时间
    if (index === 0) {
      if (prevMsg) {
        const timeDiff = msg.created_at - prevMsg.created_at
        if (timeDiff > TIME_THRESHOLD) {
          showTime = true
        }
      } else {
        showTime = true
      }
    }
    // 规则2: 与上一条消息时间间隔超过5分钟，显示时间
    else if (index > 0) {
      const prevMsg = messages[index - 1]
      const timeDiff = msg.created_at - prevMsg.created_at
      if (timeDiff > TIME_THRESHOLD) {
        showTime = true
      }
    }

    const isTodayFlag = isToday(msg.created_at)

    return {
      ...formattedMsg,
      showTime,
      formattedTime: showTime ? isTodayFlag ? dateFormater(msg.created_at, 'HH:mm') : dateFormater(msg.created_at, 'yyyy-MM-dd HH:mm') : '',
      isToday: showTime ? isTodayFlag : false
    }
  })
}

/**
 * Chat Store
 * 管理单个会话的聊天消息，提供给 /chat/:id 页面使用
 */
export const useChatStore = defineStore('chat', () => {
  const conversationStore = useConversationStore()
  // 当前会话状态
  const currentConversationId = ref(null)
  const currentConversation = ref(null)
  const messages = ref([]) // MsgBean 对象数组

  // 加载状态
  const loading = ref(false)
  const loadError = ref(null)

  // 新消息回调
  let onNewMessageCallback = null

  // 当前会话的对方用户ID
  const targetUserId = computed(() => currentConversation.value?.target_uid)

  // 判断是否为只读会话（系统通知、团队消息）
  const isReadOnlyConversation = computed(() => {
    if (!targetUserId.value) return false

    return (
      targetUserId.value === SPECIAL_USER_IDS.SYSTEM.encrypted_id ||
      targetUserId.value === SPECIAL_USER_IDS.TEAM.encrypted_id
    )
  })

  // 判断是否为特殊会话（系统通知、客服、团队消息）
  const isSpecialConversation = computed(() => {
    if (!targetUserId.value) return false
    return isReadOnlyConversation.value || targetUserId.value === SPECIAL_USER_IDS.CUSTOMER_SERVICE.encrypted_id
  })

  // 用户详情和关系信息
  const userDetail = ref(null)
  const userRelation = ref(null)
  const anchorStatus = ref(null)

  // 监听 targetUserId 变化，获取用户详情和关系信息
  watch(targetUserId, async (newTargetUserId) => {
    if (newTargetUserId) {
      try {
        // 并行获取用户详情和关系信息
        const [detailRes, relationRes, anchorStatusRes] = await Promise.all([
          getUserInfo({ member_id: newTargetUserId, scene: 'read_profile' }, { hideLoading: true }),
          getRelationWithId({ target_id: newTargetUserId }, { hideLoading: true }),
          getAnchorStatus({
            target_ids: [newTargetUserId],
            source: getAnchorStatus.SOURCE.MESSAGE_PAGE,
            scene: getAnchorStatus.SCENE.MESSAGE_PAGE,
          }, { hideLoading: true })
        ])

        // 🔧 验证：只有当前 targetUserId 匹配时才更新状态（防止快速切换时的竞态问题）
        if (targetUserId.value !== newTargetUserId) {
          console.log('[Chat Store] Target user changed during fetch, ignoring user info')
          return
        }

        userDetail.value = detailRes
        userRelation.value = relationRes
        anchorStatus.value = anchorStatusRes ? anchorStatusRes[0] : null
      } catch (error) {
        console.error('[Chat Store] Failed to fetch user info:', error)
      }
    } else {
      userDetail.value = null
      userRelation.value = null
      anchorStatus.value = null
    }
  }, { immediate: true })

  /**
   * 更新主播状态
   */
  async function updateAnchorStatus() {
    const userId = targetUserId.value
    if (!userId) return

    try {
      const anchorStatusRes = await getAnchorStatus({
        target_ids: [userId],
        source: getAnchorStatus.SOURCE.MESSAGE_PAGE,
        scene: getAnchorStatus.SCENE.MESSAGE_PAGE,
      }, { hideLoading: true })

      // 验证：只有当前 targetUserId 匹配时才更新状态
      if (targetUserId.value !== userId) {
        return
      }

      anchorStatus.value = anchorStatusRes ? anchorStatusRes[0] : null
    } catch (error) {
      console.error('[Chat Store] Failed to update anchor status:', error)
    }
  }

  /**
   * 初始化会话
   * @param {string} conversationId - 会话ID
   */
  async function initConversation(conversationId) {
    currentConversationId.value = conversationId
    messages.value = []
    loading.value = false
    loadError.value = null

    // 检查是否有预设的会话数据
    const hasPresetData = currentConversation.value?._isPreset &&
      currentConversation.value.conversation_id === conversationId

    // 从数据库获取会话
    const conversation = await conversationStore.getConversation(conversationId)
    console.log('[Chat Store] initConversation conversation:', conversation)

    // 🔧 验证：只有当前会话ID匹配时才更新状态（防止快速切换时的竞态问题）
    if (currentConversationId.value !== conversationId) {
      console.log('[Chat Store] Conversation changed during init, ignoring result')
      return
    }

    if (conversation) {
      // 会话存在，使用数据库数据
      currentConversation.value = conversation
    } else if (hasPresetData) {
      // 会话不存在但有预设数据，保留预设数据（新会话）
      // currentConversation.value 已经是预设的数据了，不需要操作
      console.log('[Chat Store] Using preset conversation data for new chat')
    } else {
      // 会话不存在且没有预设数据，抛出错误
      throw new Error('Conversation not found')
    }
  }

  /**
   * 加载会话的所有消息 - 一次性加载 ✅
   * @param {string} conversationId - 会话ID
   */
  async function loadMessages(conversationId) {
    loading.value = true

    try {
      const db = dbManager.getDatabase()
      if (!db) throw new Error('Database not initialized')

      // 一次性获取该会话的所有消息
      const cachedMessages = await db.messages
        .where('conversation_id')
        .equals(conversationId)
        .sortBy('created_at')

      // 🔧 验证：只有当前会话ID匹配时才更新状态（防止快速切换时的竞态问题）
      if (currentConversationId.value !== conversationId) {
        console.log('[Chat Store] Conversation changed during loading, ignoring messages')
        return
      }

      const messagesCount = cachedMessages.length
      console.log(`[Chat Store] Loaded ${messagesCount} messages from cache`)

      // 格式化并存储所有消息（包含时间显示逻辑）
      messages.value = formatMessagesWithTime(cachedMessages)

    } catch (error) {
      console.error('[Chat Store] Failed to load cached messages:', error)
      loadError.value = true
    } finally {
      loading.value = false
      console.log('[Chat Store] Loading complete, loading:', loading.value)
    }
  }

  /**
   * 清空会话 // ✅
   */
  function clearConversation() {
    currentConversationId.value = null
    currentConversation.value = null
    messages.value = []
    loading.value = false
    loadError.value = null
    userDetail.value = null
    userRelation.value = null
    anchorStatus.value = null
    unregisterNewMessageCallback()
  }

  /**
   * 增量添加新消息并计算时间显示（优化版，避免重新遍历所有消息）
   * @param {Array} newMessages - 新消息数组（未格式化的原始消息）
   */
  function addMessagesWithTimeDisplay(newMessages) {
    if (!newMessages || newMessages.length === 0) return

    const existingCount = messages.value.length

    // 情况1: 当前没有消息，直接格式化新消息
    if (existingCount === 0) {
      messages.value = formatMessagesWithTime(newMessages)
      return
    }

    // 情况2: 已有消息，需要处理最后一条的 showTime
    const lastExistingMsg = messages.value[existingCount - 1]

    // 格式化新消息（增量处理）
    const formattedNewMessages = formatMessagesWithTime(newMessages, lastExistingMsg)

    // 添加到消息列表
    messages.value.push(...formattedNewMessages)
  }

  /**
   * 刷新所有消息的时间显示（用于跨天场景）
   * 只更新 showTime=true 的消息的时间格式和 isToday 标记
   */
  function refreshAllMessagesTime() {
    messages.value.forEach(msg => {
      if (msg.showTime) {
        const isTodayFlag = isToday(msg.created_at)
        msg.formattedTime = isTodayFlag ? dateFormater(msg.created_at, 'hh:mm') : dateFormater(msg.created_at, 'yyyy-MM-dd hh:mm')
        msg.isToday = isTodayFlag
      }
    })
  }

  /**
   * 注册新消息回调（由 ChatPage 调用）
   * @param {Function} callback - 回调函数，参数为新消息数量
   */
  function registerNewMessageCallback(callback) {
    onNewMessageCallback = callback
  }

  /**
   * 注销新消息回调
   */
  function unregisterNewMessageCallback() {
    onNewMessageCallback = null
  }

  /**
   * 发送消息
   * @param {Object} params
   * @param {string} params.targetId - 接收者用户ID
   * @param {string} params.content - 消息内容
   * @param {string} params.msgType - 消息类型
   * @returns {Promise<Object>}
   */
  async function sendMessage({ targetId, content, msgType }) {
    const msgBean = await messageSendManager.sendMessage({
      targetId,
      content,
      msgType,
    })

    // 使用增量更新添加新消息
    addMessagesWithTimeDisplay([msgBean])

    return msgBean
  }

  /**
   * 标记会话为已读 ✅
   * @param {number} readType - 已读类型（0-进入会话, 1-停留在会话, 2-退出会话）
   */
  async function markAsRead(readType) {
    if (!currentConversationId.value) {
      return
    }
    try {
      // 立即更新本地状态
      const now = Date.now()
      await Promise.all([
        conversationStore.updateConversationReadTime(currentConversationId.value, now),
        markConversationAsRead({
          id: currentConversationId.value,
          read_type: readType
        })
      ])
    } catch (error) {
      console.error('[Chat Store] Failed to update local read status:', error)
    }
  }

  /**
   * 接收消息 ✅
   * @param {Object[]} newMessages - 新消息数组
   */
  async function receiveMessages(newMessages) {
    if (newMessages.length === 0) return

    // 标记已读
    markAsRead(MSG_READ_TYPE.STAY)

    // 获取现有消息的 msg_id 集合（用于快速查找，O(1) 复杂度）
    const existingMsgIds = new Set(messages.value.map(m => m.msg_id))

    // 过滤出不存在的消息（去重）
    // 因为发消息的时候已经push过了，但是发消息也会收到IM的消息推送，所以要做过滤
    const uniqueNewMessages = newMessages.filter(msg => !existingMsgIds.has(msg.msg_id))

    if (uniqueNewMessages.length > 0) {
      // 使用增量更新添加新消息
      addMessagesWithTimeDisplay(uniqueNewMessages)

      // 触发回调，通知 ChatPage 有新消息
      if (onNewMessageCallback) {
        onNewMessageCallback(uniqueNewMessages.length)
      }
    }
  }

  /**
   * 预设新会话数据（在跳转到聊天页面前调用）
   * @param {Object} conversationData - 会话数据
   * @param {string} conversationData.conversation_id - 会话ID
   * @param {string} conversationData.target_uid - 对方用户ID
   * @param {string} conversationData.target_avatar - 对方头像
   * @param {string} conversationData.target_nickname - 对方昵称
   */
  function presetNewConversation(conversationData) {
    currentConversation.value = {
      ...conversationData,
      last_msg_at: Date.now(),
      preview: '',
      unread_count: 0,
      rank: 0,
      is_stranger: false,
      _isPreset: true // 标记为预设数据
    }
  }

  return {
    // 状态 - 消息
    currentConversation,
    currentConversationId,
    messages,
    loading,

    // 状态 - 会话类型判断
    isReadOnlyConversation,
    isSpecialConversation,

    // 状态 - 用户信息
    userDetail,
    userRelation,
    anchorStatus,

    // 操作 - 消息
    loadMessages,
    clearConversation,
    initConversation,
    presetNewConversation,

    // 操作 - 用户状态
    updateAnchorStatus,

    // 操作 - 消息发送
    sendMessage,

    // 操作 - 消息接收
    receiveMessages,

    // 操作 - 消息已读
    markAsRead,

    // 操作 - 时间刷新
    refreshAllMessagesTime,

    // 操作 - 新消息回调
    registerNewMessageCallback,
    unregisterNewMessageCallback,
  }
})
